import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

export default function BlogCarousel() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 🔥 Curated tech images
  const techImages = [
    "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=250&fit=crop",
    "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=250&fit=crop",
    "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400&h=250&fit=crop",
    "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=250&fit=crop",
    "https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=400&h=250&fit=crop",
    "https://images.unsplash.com/photo-1537432376769-00a7c2dca71b?w=400&h=250&fit=crop",
    "https://images.unsplash.com/photo-1484417894907-623942c8ee29?w=400&h=250&fit=crop",
  ];

  // 🔥 Smart image selection
  const getTechImage = (blog, index) => {
    const title = blog.title?.toLowerCase() || "";

    if (title.includes("react") || title.includes("frontend"))
      return "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=250&fit=crop";

    if (title.includes("backend") || title.includes("api"))
      return "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=250&fit=crop";

    if (title.includes("ai") || title.includes("ml"))
      return "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop";

    return techImages[index % techImages.length];
  };

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/blogs`
        );
        const data = await res.json();

        if (data.success) {
          setBlogs(data.data || []);
        }
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Guard against duplicate/near-duplicate entries from the API so the
  // marquee doesn't visibly repeat the same card back-to-back.
  const uniqueBlogs = useMemo(() => {
    const seen = new Set();
    return blogs.filter((blog) => {
      const key = blog.slug || blog._id || blog.title;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [blogs]);

  return (
    <section className="relative py-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div className="text-center sm:text-left">
            <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              From the Blog
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Learn. Improve. Get Hired for Real
            </h2>
            <p className="text-gray-500 mt-3 text-sm md:text-base">
              Real insights, real strategies — no fluff.
            </p>
          </div>

          {!loading && uniqueBlogs.length > 0 && (
            <button
              onClick={() => navigate("/blogs")}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 shrink-0"
            >
              View all articles
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Loading / Empty */}
      {loading ? (
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-6 overflow-hidden">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="min-w-[320px] max-w-[320px] bg-white rounded-2xl border border-gray-100 shadow-sm animate-pulse"
              >
                <div className="h-44 bg-gray-100 rounded-t-2xl" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-gray-100 rounded w-5/6" />
                  <div className="h-4 bg-gray-100 rounded w-3/5" />
                  <div className="h-3 bg-gray-100 rounded w-2/5 mt-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : uniqueBlogs.length === 0 ? (
        <div className="max-w-7xl mx-auto px-6 text-center text-gray-400">
          No blogs available
        </div>
      ) : (
        <div className="relative">
          {/* Edge fades so cards never look like they're bleeding off-screen */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-10 sm:w-24 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 sm:w-24 bg-gradient-to-l from-white to-transparent z-10" />

          <div className="overflow-hidden">
            <div className="flex gap-6 animate-scroll px-4">
              {[...uniqueBlogs, ...uniqueBlogs].map((blog, index) => {
                const image = blog.coverImage || getTechImage(blog, index);
                const tag = blog.tags?.[0];

                return (
                  <div
                    key={`${blog.slug || blog._id}-${index}`}
                    onClick={() => navigate(`/blogs/${blog.slug}`)}
                    className="group min-w-[300px] max-w-[300px] sm:min-w-[320px] sm:max-w-[320px] bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-2xl hover:border-blue-200 transition-all duration-300 cursor-pointer hover:-translate-y-2 flex flex-col"
                  >
                    {/* Image */}
                    <div className="relative overflow-hidden rounded-t-2xl shrink-0">
                      <img
                        src={image}
                        alt={blog.title}
                        className="w-full h-44 object-cover group-hover:scale-110 transition duration-500"
                        onError={(e) => {
                          e.target.src =
                            techImages[(index + 1) % techImages.length];
                        }}
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                      {tag && (
                        <span className="absolute top-3 left-3 max-w-[85%] truncate bg-white/90 backdrop-blur-sm text-gray-800 text-xs px-2.5 py-1 rounded-full font-medium shadow-sm">
                          {tag}
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 mb-2 line-clamp-2">
                        {blog.title}
                      </h3>

                      <p className="text-sm text-gray-600 line-clamp-3 flex-1">
                        {stripHtml(blog.excerpt || blog.content)}
                      </p>

                      <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                        <span className="truncate">{blog.author || "RealHired"}</span>
                        {blog.createdAt && (
                          <span className="shrink-0">
                            {new Date(blog.createdAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      <div className="mt-4 flex items-center gap-1 text-blue-600 text-sm font-medium group-hover:gap-2 transition-all">
                        Read Article
                        <ArrowRight size={14} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Animation */}
      <style>
        {`
          @keyframes scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }

          .animate-scroll {
            width: max-content;
            animation: scroll 28s linear infinite;
          }

          .animate-scroll:hover {
            animation-play-state: paused;
          }
        `}
      </style>
    </section>
  );
}

function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").slice(0, 140) + "...";
}
